import { TFunction } from 'i18next';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { queryClient } from '~/client/queryClient';
import { snack } from '~/hooks/ToastProvider';
import { useLoggedUser } from '~/services/user';
import { useSibillSocket } from '~/state/notifications/socket';
import { DocumentQueryKeys } from '~/types/document';
import { downloadUrl } from '~/utils/download';
import { clearExportingVATBooks } from '~/views/bookkeeping/settlement/settlement-state';
import { SibillSocketChannel } from '../sibill-socket';

interface UserChannelDeps {
  t: TFunction<'notifications', undefined>;
}

const userChannelName = (userId: string) => `user:${userId}`;

interface BackendMessagePayload {
  message: string;
}

const backendMessage = (payload: BackendMessagePayload) => {
  snack.enqueue(
    {
      message: payload.message,
      type: 'error',
    },
    { timeout: 10000 },
  );
};

interface BookkeepingVatBooksReportReadyPayload {
  company_id: string;
  tracing_id: string;
  pdf_url: string;
}

const bookkeepingVatBooksReportReady = (payload: BookkeepingVatBooksReportReadyPayload) => {
  const cleared = clearExportingVATBooks(payload.tracing_id);
  if (!cleared) {
    return;
  }

  downloadUrl(payload.pdf_url);
};

interface BookkeepingVatBooksReportErrorPayload {
  company_id: string;
  tracing_id: string;
}

const bookkeepingVatBooksReportError = (payload: BookkeepingVatBooksReportErrorPayload, { t }: UserChannelDeps) => {
  const cleared = clearExportingVATBooks(payload.tracing_id);
  if (!cleared) {
    return;
  }

  snack.enqueue(
    {
      message: t('bookkeeping_vat_books_pdf_error.message'),
      type: 'error',
    },
    { timeout: 10000 },
  );
};

interface DocumentsImportFailedRow {
  row: number;
  reference: string;
  errors: string;
}

export interface DocumentsImportCompletedPayload {
  status: 'success' | 'error';
  imported?: number;
  failed?: number;
  failed_rows?: DocumentsImportFailedRow[];
  fetch_failed?: boolean;
}

const formatFailedRows = (rows: DocumentsImportFailedRow[]) =>
  rows.map((row) => `#${row.row} (${row.reference}): ${row.errors}`).join('\n');

export const documentsImportCompleted = (payload: DocumentsImportCompletedPayload, { t }: UserChannelDeps) => {
  if (payload.status === 'success') {
    queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
    snack.enqueue(
      {
        message: t('documents_import_completed.success', { count: payload.imported ?? 0 }),
        type: 'success',
      },
      { timeout: 3000 },
    );
    return;
  }

  // Some rows were imported and others failed: refresh the list and tell the
  // user exactly which rows to fix.
  if (payload.imported) {
    queryClient.invalidateQueries({ queryKey: DocumentQueryKeys.all });
  }

  const failedRows = payload.failed_rows ?? [];
  let message = t('documents_import_completed.error');
  if (payload.fetch_failed) {
    message = t('documents_import_completed.fetch_failed');
  } else if (failedRows.length) {
    message = t('documents_import_completed.error_with_rows', {
      count: payload.failed ?? failedRows.length,
      imported: payload.imported ?? 0,
      rows: formatFailedRows(failedRows),
    });
  }

  snack.enqueue(
    {
      message,
      type: 'error',
    },
    { timeout: 10000 },
  );
};

const registerUserChannelListeners = (channel: SibillSocketChannel, deps: UserChannelDeps) => {
  channel.registerEvent('BACKEND_MESSAGE', (payload: BackendMessagePayload) => backendMessage(payload));
  channel.registerEvent('DOCUMENTS_IMPORT_COMPLETED', (payload: DocumentsImportCompletedPayload) =>
    documentsImportCompleted(payload, deps),
  );
  channel.registerEvent('BOOKKEEPING_VAT_BOOKS_REPORT_READY', (payload: BookkeepingVatBooksReportReadyPayload) =>
    bookkeepingVatBooksReportReady(payload),
  );
  channel.registerEvent('BOOKKEEPING_VAT_BOOKS_REPORT_ERROR', (payload: BookkeepingVatBooksReportErrorPayload) =>
    bookkeepingVatBooksReportError(payload, deps),
  );
};

export const useUserSocketConnection = (changeConnectionStatus: boolean) => {
  const userChannel = useRef<SibillSocketChannel | undefined>(undefined);
  const { t } = useTranslation('notifications');

  const { joinChannel } = useSibillSocket();
  const { data: user } = useLoggedUser();

  useEffect(() => {
    if (!user?.id) return;

    userChannel.current = joinChannel(userChannelName(user.id));
    if (!userChannel.current) return;

    registerUserChannelListeners(userChannel.current, { t });

    return () => userChannel.current?.leave();
  }, [user?.id, joinChannel, changeConnectionStatus, t]);

  return userChannel;
};
